import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Copy, Plus, Settings2, Trash2, Check, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAdmin } from "@/context/AdminContext";
import { api, errText } from "@/lib/api";
import { BLOCK_TYPES, PAD, WIDTH_CLASS, cloneBlock, newBlock } from "./blockConfig";
import { BlockView } from "./BlockView";
import { BlockSettings } from "./BlockSettings";

const AddBlock = ({ onAdd, testId, big }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={testId} className={big
          ? "flex w-full items-center justify-center gap-2 border-2 border-dashed border-[#E62314]/60 py-6 text-sm font-bold text-[#E62314] transition-colors hover:bg-[#E62314]/5"
          : "flex h-8 w-8 items-center justify-center rounded-full bg-[#E62314] text-white shadow hover:scale-110"} aria-label="Add block">
          <Plus className="h-4 w-4" />{big && "Add content"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="z-[70] w-56 rounded-none border-black/10 bg-[#FBF9F5] p-1">
        {Object.entries(BLOCK_TYPES).map(([k, t]) => (
          <button key={k} onClick={() => { onAdd(k); setOpen(false); }} data-testid={`add-block-${k}`}
            className="block w-full px-3 py-2.5 text-left text-sm font-semibold hover:bg-[#ECEAE6]">{t.label}</button>
        ))}
      </PopoverContent>
    </Popover>
  );
};

const Toolbar = ({ i, count, act }) => {
  const b = "flex h-8 w-8 items-center justify-center text-white hover:bg-[#E62314] disabled:opacity-30";
  return (
    <div className="absolute -top-4 right-2 z-10 flex items-center overflow-hidden rounded-full bg-[#0A0A0A] shadow-lg" data-testid={`block-toolbar-${i}`}>
      <button className={b} onClick={() => act("settings")} aria-label="Settings" data-testid={`block-settings-${i}`}><Settings2 className="h-4 w-4" /></button>
      <button className={b} disabled={i === 0} onClick={() => act("up")} aria-label="Move earlier" data-testid={`block-up-${i}`}><ArrowUp className="h-4 w-4" /></button>
      <button className={b} disabled={i === count - 1} onClick={() => act("down")} aria-label="Move later" data-testid={`block-down-${i}`}><ArrowDown className="h-4 w-4" /></button>
      <button className={b} onClick={() => act("copy")} aria-label="Duplicate" data-testid={`block-copy-${i}`}><Copy className="h-4 w-4" /></button>
      <button className={b} onClick={() => act("delete")} aria-label="Delete" data-testid={`block-delete-${i}`}><Trash2 className="h-4 w-4" /></button>
    </div>
  );
};

export const BlockArea = ({ slug, className = "", emptyHint }) => {
  const { editing } = useAdmin();
  const [blocks, setBlocks] = useState([]);
  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState("saved");
  const timer = useRef(null);

  useEffect(() => {
    let alive = true;
    api.get(`/pages/${slug}`).then((r) => { if (alive) setBlocks(Array.isArray(r.data?.blocks) ? r.data.blocks : []); }).catch(() => { if (alive) setBlocks([]); });
    return () => { alive = false; };
  }, [slug]);

  const persist = useCallback((next) => {
    setStatus("saving");
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try { await api.put(`/pages/${slug}`, { blocks: next }); setStatus("saved"); }
      catch (e) { setStatus("error"); toast.error(errText(e, "Couldn't save your changes")); }
    }, 700);
  }, [slug]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const update = (next) => { setBlocks(next); persist(next); };
  const change = (id, patch) => update(blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const insert = (type, at) => { const nb = newBlock(type); const n = [...blocks]; n.splice(at, 0, nb); update(n); if (!["heading", "text", "spacer"].includes(type)) setSelected(nb.id); };
  const act = (i, what) => {
    const n = [...blocks];
    if (what === "settings") return setSelected(blocks[i].id);
    if (what === "up") [n[i - 1], n[i]] = [n[i], n[i - 1]];
    if (what === "down") [n[i + 1], n[i]] = [n[i], n[i + 1]];
    if (what === "copy") n.splice(i + 1, 0, cloneBlock(blocks[i]));
    if (what === "delete") { if (!window.confirm("Delete this block?")) return; n.splice(i, 1); }
    update(n);
  };

  if (!editing && !blocks.length) return null;
  const sel = blocks.find((b) => b.id === selected) || null;

  return (
    <section data-testid={`block-area-${slug}`} className={`mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 ${className}`}>
      {editing && (
        <div className="mb-4 flex items-center gap-2 font-mono-label text-xs text-[#57534E]" data-testid="block-area-status">
          {status === "saving" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
          {status === "saving" ? "Saving…" : status === "error" ? "Not saved" : "All changes saved"}
          {emptyHint && !blocks.length && <span className="ml-2">{emptyHint}</span>}
        </div>
      )}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-6">
        {blocks.map((b, i) => (
          <div key={b.id} data-testid={`block-${i}`} style={{ background: b.bg || undefined, padding: PAD[b.pad] }}
            className={`relative col-span-1 ${WIDTH_CLASS[b.width] || "md:col-span-6"} ${editing ? "outline-dashed outline-1 outline-black/20 hover:outline-[#E62314]" : ""}`}>
            {editing && <Toolbar i={i} count={blocks.length} act={(w) => act(i, w)} />}
            <BlockView b={b} editing={editing} onChange={(patch) => change(b.id, patch)} />
            {editing && <div className="absolute -bottom-4 left-1/2 z-10 -translate-x-1/2"><AddBlock onAdd={(t) => insert(t, i + 1)} testId={`add-after-${i}`} /></div>}
          </div>
        ))}
      </div>
      {editing && !blocks.length && <AddBlock big onAdd={(t) => insert(t, 0)} testId={`add-first-${slug}`} />}
      <BlockSettings block={sel} onChange={(patch) => sel && change(sel.id, patch)} onClose={() => setSelected(null)} />
    </section>
  );
};
